import { useCallback, useEffect, useRef, useState } from 'react'
import { KeyRound, PhoneIncoming } from 'lucide-react'
import { Link } from '../router.js'
import { adminMutate, apiRead, usePermissions } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import { messageErreur, pastilleConnexion, quandParis, type Connexion } from './interphone-model.js'

/**
 * ADMINISTRATION › INTÉGRATIONS › INTERPHONE (22/10/2026).
 *
 * Version simple du modèle Sonde : l'adresse du service Interphone (une
 * VALEUR → la Save Bar) et le jeton de Gestion (un GESTE → boutons « Tester »
 * puis « Enregistrer », confirmation d'identité). Le jeton n'est jamais
 * réaffiché : le champ est toujours vide au départ. Service absent ou muet :
 * la page reste utilisable et le dit en gris.
 */

type Lecture = { etat: 'chargement' } | { etat: 'ok'; data: Connexion } | { etat: 'erreur'; message: string }

// Ports refusés par fetch (norme Fetch, « bad port ») : copie de
// FETCH_PORTS_INTERDITS (packages/core/src/interphone-service/config.ts),
// le web n'important pas le cœur. L'API refuse de toute façon.
const PORTS_INTERDITS = new Set([
  1, 7, 9, 11, 13, 15, 17, 19, 20, 21, 22, 23, 25, 37, 42, 43, 53, 69, 77, 79, 87, 95, 101, 102, 103, 104, 109, 110, 111, 113, 115, 117, 119, 123, 135, 137, 139, 143, 161, 179, 389, 427, 465, 512, 513, 514, 515, 526, 530, 531, 532, 540, 548, 554, 556, 563, 587, 601, 636, 989, 990, 993, 995, 1719, 1720, 1723, 2049, 3659, 4045, 4190, 5060, 5061, 6000, 6566, 6665, 6666, 6667, 6668, 6669, 6679, 6697, 10080,
])

const validerAdresse = (v: string): string | null => {
  if (!v) return 'L’adresse du service est requise (par exemple http://interphone-api:4195).'
  try {
    const u = new URL(v)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'L’adresse doit commencer par http:// ou https://.'
    if (u.username || u.password || u.search || u.hash) return 'L’adresse ne doit contenir ni identifiant, ni requête, ni fragment.'
    if (PORTS_INTERDITS.has(u.port ? Number(u.port) : (u.protocol === 'https:' ? 443 : 80))) return `Le port ${u.port} est bloqué par les navigateurs et par Gestion : choisissez-en un autre (par exemple 4195).`
  } catch { return 'Adresse invalide (par exemple http://interphone-api:4195).' }
  return null
}

export function InterphoneIntegrationPage() {
  const { permissions } = usePermissions()
  const peutModifier = permissions.includes('interphone.credentials.manage')
  const [l, setL] = useState<Lecture>({ etat: 'chargement' })
  const [adresse, setAdresse] = useState('')
  const adresseLue = useRef('')

  const appliquer = useCallback((c: Connexion) => {
    setL({ etat: 'ok', data: c })
    const lue = c.api_url ?? ''
    const precedente = adresseLue.current
    adresseLue.current = lue
    // Une relecture n'écrase pas une adresse en cours de saisie.
    setAdresse((brouillon) => (brouillon === precedente ? lue : brouillon))
  }, [])
  const charger = useCallback(() => {
    void apiRead<Connexion>('/api/v1/interphone/connexion')
      .then((r) => { if (r.status === 200) appliquer(r.data); else setL({ etat: 'erreur', message: messageErreur(r.status, r.data, 'Le raccordement est illisible.') }) })
      .catch(() => setL({ etat: 'erreur', message: 'Gestion n’a pas répondu.' }))
  }, [appliquer])
  useEffect(() => { charger() }, [charger])

  const lue = l.etat === 'ok' ? l.data.api_url ?? '' : ''
  const propre = adresse.trim().replace(/\/+$/, '')
  const dirty = peutModifier && l.etat === 'ok' && propre !== lue
  useSaveBar({
    dirty,
    invalid: dirty ? validerAdresse(propre) : null,
    saveLabel: 'Enregistrer l’adresse',
    successMessage: 'Adresse du service Interphone enregistrée.',
    onSave: async () => {
      const r = await adminMutate<Connexion & { error?: string; message?: string }>('PUT', '/api/v1/interphone/connexion', { api_url: propre })
      if (r.status === 200) { appliquer(r.data); setAdresse(r.data.api_url ?? ''); return true }
      if (r.data.error === 'STEPUP_REQUIRED') return 'Confirmation d’identité annulée : rien n’a été enregistré.'
      return messageErreur(r.status, r.data, 'L’adresse n’a pas été enregistrée.')
    },
    onCancel: () => setAdresse(lue),
  })

  const c = l.etat === 'ok' ? l.data : null
  // « Jeton refusé » n'est pas « Injoignable » : l'un se règle ici, l'autre sur le serveur.
  const p = pastilleConnexion(c)
  const pastille = { cls: `lc-pill--${p.ton}`, texte: p.texte }

  return (
    <main className="lc-page lc-page--readable lc-stagger ip-page">
      <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
      <h1 className="lc-title">Interphone IA (Sacha)</h1>
      <p className="lc-subtitle">
        Le service Interphone garde le journal des appels, la voix des visiteurs et les réglages de Sacha.
        Gestion s’y raccorde avec un jeton, côté serveur, et n’en garde aucune copie.
      </p>

      <section className="lc-panel ip-section" aria-labelledby="ip-raccord">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="ip-raccord"><PhoneIncoming size={16} aria-hidden="true" /> Connexion</h2>
          <span className={`lc-pill ${pastille.cls}`}>{pastille.texte}</span>
        </header>
        <div className="lc-panel__body ip-reglages">
          {l.etat === 'chargement' ? <p className="lc-hint" aria-busy="true">Lecture du raccordement…</p>
            : l.etat === 'erreur' ? <p className="ip-gris" role="status">{l.message}</p> : null}
          {c ? (
            <>
              <Field label="Adresse du service" helper="Réseau Docker du serveur. Le jeton voyage dans l’en-tête, jamais dans l’adresse.">
                <TextInput value={adresse} onChange={setAdresse} disabled={!peutModifier} placeholder="http://interphone-api:4195" ariaLabel="Adresse du service Interphone" />
              </Field>
              {c.status.reachable ? (
                <p className="cle-verdict cle-verdict--ok" role="status">
                  Le service répond{c.status.agent_en_ligne === null ? '.' : c.status.agent_en_ligne ? ' et Sacha est en ligne.' : ', mais Sacha est hors ligne.'}
                </p>
              ) : c.status.message ? <p className={`cle-verdict${c.status.error_code === 'INTERPHONE_TOKEN_REFUSED' ? ' cle-verdict--ko' : ''}`} role="status">{c.status.message}</p> : null}
              {c.flux ? (
                <dl className="cle-faits">
                  <div>
                    <dt>Flux d’événements</dt>
                    <dd>
                      {c.flux.en_panne
                        ? `Interrompu depuis ${quandParis(c.flux.echec_depuis)}${c.flux.echec_message ? ` — ${c.flux.echec_message}` : ''}`
                        : c.flux.dernier_succes ? `Lu ${quandParis(c.flux.dernier_succes)}` : 'Pas encore lu par le worker.'}
                    </dd>
                  </div>
                </dl>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {c ? <JetonPanneau etat={c.token} peutModifier={peutModifier} onEnregistre={appliquer} /> : null}
    </main>
  )
}

function JetonPanneau({ etat, peutModifier, onEnregistre }: {
  etat: { set: boolean; updated_at: string | null }
  peutModifier: boolean
  onEnregistre: (c: Connexion) => void
}) {
  const confirm = useConfirm()
  // Toujours vide au départ : la valeur ne vient JAMAIS du serveur.
  const [jeton, setJeton] = useState('')
  const [essai, setEssai] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [verdict, setVerdict] = useState<{ ok: boolean | null; texte: string; pour: string } | null>(null)
  const [apres, setApres] = useState<{ ok: boolean | null; texte: string } | null>(null)
  const valeur = jeton.trim()
  const tropCourt = valeur.length < 16
  const courant = verdict && verdict.pour === valeur ? verdict : null

  const tester = async () => {
    setEssai(true); setApres(null)
    try {
      const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>('POST', '/api/v1/interphone/jeton/test', { token: valeur })
      if (r.status === 200 && typeof r.data.ok === 'boolean') setVerdict({ ok: r.data.ok, texte: r.data.message ?? '', pour: valeur })
      else setVerdict({ ok: null, texte: messageErreur(r.status, r.data, 'L’essai n’a pas eu lieu.'), pour: valeur })
    } catch { setVerdict({ ok: null, texte: 'Gestion n’a pas répondu : l’essai n’a pas eu lieu.', pour: valeur }) } finally { setEssai(false) }
  }

  const enregistrer = async () => {
    if (courant?.ok !== true) {
      const ok = await confirm({
        title: 'Enregistrer un jeton qui n’a pas été reconnu ?',
        message: courant
          ? `Le dernier essai n’a pas abouti : ${courant.texte} S’il est faux, Gestion ne pourra plus lire le journal de l’interphone.`
          : 'Ce jeton n’a pas été essayé. S’il est faux, Gestion ne pourra plus lire le journal de l’interphone. Mieux vaut cliquer d’abord sur « Tester ».',
        variant: 'sensitive', confirmLabel: 'Enregistrer quand même',
      })
      if (!ok) return
    }
    setEnvoi(true); setApres(null)
    try {
      const r = await adminMutate<Connexion & { error?: string; message?: string }>('PUT', '/api/v1/interphone/jeton', { token: valeur })
      if (r.status === 200) {
        // Vidé AVANT tout le reste : le secret ne séjourne pas dans la page.
        setJeton(''); setVerdict(null)
        setApres({ ok: true, texte: 'Jeton rangé au coffre de Gestion. Il ne sera jamais réaffiché.' })
        onEnregistre(r.data)
        return
      }
      setApres({ ok: r.data.error === 'STEPUP_REQUIRED' ? null : false, texte: r.data.error === 'STEPUP_REQUIRED' ? 'Confirmation d’identité annulée : rien n’a été enregistré.' : messageErreur(r.status, r.data, 'Le jeton n’a pas été enregistré.') })
    } catch { setApres({ ok: null, texte: 'Gestion n’a pas répondu : relisez la page avant de réessayer.' }) } finally { setEnvoi(false) }
  }

  const ton = (ok: boolean | null) => (ok === true ? ' cle-verdict--ok' : ok === false ? ' cle-verdict--ko' : '')
  return (
    <section className="lc-panel cle-panel ip-section" aria-labelledby="ip-jeton">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="ip-jeton"><KeyRound size={16} aria-hidden="true" /> Jeton de Gestion</h2>
        <span className={`lc-pill ${etat.set ? 'lc-pill--ok' : 'lc-pill--idle'}`}>{etat.set ? 'Enregistré' : 'Absent'}</span>
      </header>
      <div className="lc-panel__body cle-corps">
        <dl className="cle-faits">
          <div>
            <dt>Jeton actuel</dt>
            <dd>{etat.set ? `Enregistré${etat.updated_at ? ` ${quandParis(etat.updated_at)}` : ''}` : 'Aucun jeton : Gestion ne peut pas encore lire l’interphone.'}</dd>
          </div>
        </dl>
        {peutModifier ? (
          <div className="cle-saisie">
            <label className="cle-champ">
              <span>Nouveau jeton</span>
              <input className="lc-input" type="password" autoComplete="new-password" spellCheck={false} name="interphone-jeton"
                value={jeton} aria-label="Nouveau jeton du service Interphone"
                onChange={(e) => { setJeton(e.target.value); setApres(null) }} />
            </label>
            <div className="cle-actions">
              <button type="button" className="lc-btn" disabled={tropCourt || essai || envoi} onClick={() => { void tester() }}>{essai ? 'Essai en cours…' : 'Tester'}</button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={tropCourt || essai || envoi} onClick={() => { void enregistrer() }}>{envoi ? 'Enregistrement…' : 'Enregistrer le jeton'}</button>
            </div>
            {courant ? <p role="status" className={`cle-verdict${ton(courant.ok)}`}>{courant.texte}</p> : null}
          </div>
        ) : <p className="lc-hint">Seul un compte autorisé à gérer l’accès au service Interphone peut remplacer ce jeton.</p>}
        {apres ? <p role="status" className={`cle-verdict${ton(apres.ok)}`}>{apres.texte}</p> : null}
      </div>
    </section>
  )
}
